import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText, Field, Row, Text } from '../components/Form'
import ImageDrop from '../components/ImageDrop'
import { createTour, updateTour, deleteTour } from '../lib/mutations'
import type { TourDraft } from '../lib/mutations'
import { toDateInputValue } from '../lib/zonedTime'
import type { Tour, WithId } from '../types/models'

export default function TourDialog({
  open,
  tour,
  ownerId,
  onClose,
  onSaved,
  onDeleted,
}: {
  open: boolean
  tour?: WithId<Tour> | null
  ownerId: string
  onClose: () => void
  onSaved?: (tourId: string) => void
  onDeleted?: () => void
}) {
  const [draft, setDraft] = useState<TourDraft>(() => initialDraft(tour))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // Saving mid-upload would write a poster URL that doesn't exist yet.
  const [uploading, setUploading] = useState(false)

  const patch = (next: Partial<TourDraft>) => setDraft((previous) => ({ ...previous, ...next }))

  const valid =
    draft.tourName.trim().length > 0 &&
    draft.artist.trim().length > 0 &&
    draft.startDate.length > 0 &&
    draft.endDate.length > 0 &&
    draft.endDate >= draft.startDate

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      if (tour) {
        await updateTour(tour.id, draft)
        onSaved?.(tour.id)
      } else {
        const id = await createTour(draft, ownerId)
        onSaved?.(id)
      }
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!tour) return
    setBusy(true)
    setError(null)
    try {
      await deleteTour(tour.id)
      onDeleted?.()
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={tour ? 'Edit tour' : 'New tour'}
      onClose={onClose}
      footer={
        <>
          {tour && (
            <button
              className="btn btn-danger"
              disabled={busy}
              style={{ marginRight: 'auto' }}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete tour
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={!valid || busy || uploading}
          >
            {uploading ? 'Uploading…' : busy ? 'Saving…' : tour ? 'Save' : 'Create tour'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Tour name">
          <Text
            value={draft.tourName}
            onChange={(value) => patch({ tourName: value })}
            autoFocus
          />
        </Field>

        <Field label="Artist">
          <Text
            value={draft.artist}
            onChange={(value) => patch({ artist: value })}
          />
        </Field>

        <Row>
          <Field label="Starts">
            <Text type="date" value={draft.startDate} onChange={(value) => patch({ startDate: value })} />
          </Field>
          <Field label="Ends">
            <Text type="date" value={draft.endDate} onChange={(value) => patch({ endDate: value })} />
          </Field>
        </Row>

        {draft.endDate && draft.startDate && draft.endDate < draft.startDate && (
          <ErrorText>The tour can't end before it starts.</ErrorText>
        )}

        <ImageDrop
          value={draft.posterURL}
          onChange={(url) => patch({ posterURL: url })}
          onBusyChange={setUploading}
        />

        <ErrorText>{error}</ErrorText>

        {confirmingDelete && tour && (
          <div
            className="stack"
            style={{
              gap: 8,
              padding: 14,
              borderRadius: 'var(--radius-control)',
              background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            }}
          >
            <strong>Delete {tour.tourName}?</strong>
            <p className="t-caption">
              This removes the tour and every show, itinerary item, flight, hotel and crew record
              on it. Firestore has no undo for this.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-danger" onClick={() => void remove()} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete permanently'}
              </button>
              <button className="btn btn-quiet" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function initialDraft(tour?: WithId<Tour> | null): TourDraft {
  if (!tour) {
    const today = toDateInputValue(new Date())
    return { tourName: '', artist: '', startDate: today, endDate: today, posterURL: '' }
  }
  return {
    tourName: tour.tourName,
    artist: tour.artist,
    startDate: toDateInputValue(tour.startDate.toDate()),
    endDate: toDateInputValue(tour.endDate.toDate()),
    posterURL: tour.posterURL ?? '',
  }
}
